import axios from 'utils/axios';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IUser, IUserId } from '../types/user.interface';

type HookNotReadyResult = { ready: false };
type HookReadyResult<T extends object> = { ready: true } & T;
type HookResult<T extends object> = HookReadyResult<T> | HookNotReadyResult;

export type UseCurrentUserState = HookResult<{
    updatePassword(
        values: any
    ): void;
    updateUserProfile(values: any): void;
    updateInProgress: boolean;
    logout(): void;
    user: any;
}>;

export const useCurrentUser = (fullProfile?: boolean) => {
    const [state, setState] = useState<UseCurrentUserState>({
        ready: false
    });

    // FIXME: This is a poor implementation; should separate actions as proper callbacks and handle start readiness outside of the effect and actually handle errors.
    useEffect(() => {
        (async () => {
            const response = await axios.get(fullProfile ? '/berryAPI/getProfile' : '/berryAPI/me'); // Fetch the user's own profile

            const logout = async () => {
                setState((s) => ({ ...s, updateInProgress: true }));
                await axios.post('/logout');
                setState({ ready: false });
                document.location.href = '/';
            };

            const updatePassword = async (
                values: any
            ) => {
                setState((s) => ({ ...s, updateInProgress: true }));
                await axios
                    .post('/berryAPI/updatePassword', values)
                    .then((updateResponse) => {
                        setState((s) => ({
                            ...s,
                            updateInProgress: false
                        }));
                    })
                    .catch((error) => {
                      setState(s => ({ ...s, updateInProgress: false }));
                      throw error;
                    });
            };

            // Allow users to update their own profiles
            const updateUserProfile = async (
                values: any
            ) => {
                setState((s) => ({ ...s, updateInProgress: true }));
                await axios
                    .post('/berryAPI/user/editPersonalProfile', values)
                    .then((response) => {
                        setState((s) => ({
                            ...s,
                            updateInProgress: false
                        }));
                    })
                    .catch((error) => {
                      setState(s => ({ ...s, updateInProgress: false }));
                      throw error;
                    });
            };

            setState({
                ready: true,
                updateInProgress: false,
                updatePassword,
                updateUserProfile,
                logout,
                user: response.data
            });
        })();
    }, []);

    return state;
};

// export type UseUserState = HookResult<{

//     updateUser(
//         userFields: IUser,
//         setErrors: ({}) => void,
//         setTouched: ({}) => void,
//         setStatus: ({}) => void,
//         resetForm: ({}) => void
//     ): void;
//     updateUserInProgress: boolean;
//     user: IUser;
//     regions: IRegions;
// }>;

export interface UseUserState {
    createInProgress: boolean
    createUser(data: any, profilePhoto?: File): void
    updateInProgress: boolean
    updateUser(data: any, returnPath?: string): void
    approvePendingProfileChanges(data: any): void
    deleteInProgress: boolean
    deleteUser(data: any): void
    approveInProgress: boolean
    approveUser(data: any): void
    rejectInProgress: boolean
    rejectUser(data: any): void
    reactivateInProgress: boolean
    reactivateUser(data: any): void
    fetchInProgress: boolean
    fetch(data: any): void
    profilePhotoInProgress: boolean
    uploadProfilePhoto(id: number, photo: File): Promise<string | null>
    deleteProfilePhoto(id: number): Promise<null>
    resendSetupEmail: ((email: string) => Promise<any>) | null
    user: IUser;
}

export const useUser = () => {

    const navigate = useNavigate();
    // const usersState = useUsers();

    const [state, setState] = useState<UseUserState>({
        // ready: false,
        createInProgress: false,
        createUser: null,
        updateInProgress: false,
        updateUser: null,
        approvePendingProfileChanges: null,
        deleteInProgress: false,
        deleteUser: null,
        approveInProgress: false,
        approveUser: null,
        rejectInProgress: false,
        rejectUser: null,
        reactivateInProgress: false,
        reactivateUser: null,
        fetchInProgress: false,
        fetch: null,
        profilePhotoInProgress: false,
        uploadProfilePhoto: null,
        deleteProfilePhoto: null,
        resendSetupEmail: null,
        user: null,
    });
    
    const fetch = useCallback(async (userID?: IUserId) => {
        setState(s => ({ ...s, fetchInProgress: true }))
        let response = await axios.get<any>(`/berryAPI/user/${userID}`);
        setState(s => ({
          ...s,
          fetchInProgress: false,
          user: response.data.user
        }))
    }, [setState])

    const uploadProfilePhoto = useCallback(async (id: number, photo: File) => {
        setState(s => ({ ...s, profilePhotoInProgress: true }));
        const formData = new FormData();
        formData.append('userId', id.toString());
        formData.append('photo', photo);

        try {
          const response = await axios.post('/berryAPI/user/updateProfilePhoto', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const imageUrl = response.data?.imageUrl ?? null;
          setState(s => ({
            ...s,
            profilePhotoInProgress: false,
            user: s.user ? { ...s.user, image_url: imageUrl } : s.user,
          }));
          return imageUrl;
        } catch (error) {
          setState(s => ({ ...s, profilePhotoInProgress: false }));
          throw error;
        }
      }, [setState]);

    const createUser = useCallback(
        async (data: any, profilePhoto?: File) => {
          let path = '';
          if (data.role == "Food Donor") {
            path = 'donors';
          } else if (data.role == "Recipient") {
            path = 'recipients';
          } else if (data.role == "Delivery Driver") {
            path = 'delivery-drivers';
          } else if (data.role == "Admin") {
            path = 'administrators';
          }
          setState(s => ({ ...s, createInProgress: true }))
          try {
            const response = await axios.post('/berryAPI/user/create', data, {});
            if (response.status == 200) {
              const userId = response.data?.userId;
              if (profilePhoto && userId) {
                await uploadProfilePhoto(userId, profilePhoto);
              }
              navigate(`/${path}`);
            }
          } catch (error) {
            console.log(error);
            setState(s => ({ ...s, createInProgress: false }));
            throw error;
          }
        },
        [setState, navigate, uploadProfilePhoto]
      )

      // Allow Admins to update user profiles
      const updateUser = useCallback(
        async (data: any, returnPath?: string) => {
          let path = '';
          if (data.role == "Food Donor") {
            path = 'donors';
          } else if (data.role == "Recipient") {
            path = 'recipients';
          } else if (data.role == "Delivery Driver") {
            path = 'delivery-drivers';
          } else if (data.role == "Admin") {
            path = 'administrators';
          }
          setState(s => ({ ...s, updateInProgress: true }));
          return await axios.post('/berryAPI/user/edit', data, {}).then((response)=>{
            if (response.status == 200) {
              navigate(returnPath ?? `/${path}`);
            }
          }).catch((error) => {
            // Error
            console.log(error);
            setState(s => ({ ...s, updateInProgress: false }))
            throw error;
        });
        
        },
        [setState, navigate],
      )

      // Allow Admins to approve or reject pending changes on user profiles
      const approvePendingProfileChanges = useCallback(
        async (id: any, approveDonorPreferredRecipients: string) => {
          setState(s => ({ ...s, updateInProgress: true }));
            return await axios.post('/berryAPI/user/approvePendingProfileChanges', {}, {
              params: {
                id: id,
                approveDonorPreferredRecipients: approveDonorPreferredRecipients
              }
            }).then((response)=>{
          }).catch((error) => {
            // Error
            console.log(error);
            setState(s => ({ ...s, updateInProgress: false }))
            throw error;
          });
        },
        [setState],
      )

      const deleteUser = useCallback(
        async (id: any, isSuperAdmin: boolean = false) => {
          setState(s => ({ ...s, deleteInProgress: true }))
          await axios.post(isSuperAdmin ? '/berryAPI/superadmin/delete' : '/berryAPI/user/delete',{}, {
            params: {id}
          })
          setState(s => ({ ...s, deleteInProgress: false }))
        },
        [setState],
      )

      const approveUser = useCallback(
        async (id: any) => {
          setState(s => ({ ...s, approveInProgress: true }))
          await axios.post('/berryAPI/user/approve',{}, {
            params: {id}
          })
          setState(s => ({ ...s, approveInProgress: false }))
        },
        [setState],
      )

      const rejectUser = useCallback(
        async (id: any) => {
          setState(s => ({ ...s, rejectInProgress: true }))
          try {
            await axios.post('/berryAPI/user/reject',{}, {
              params: {id}
            })
          } catch (error) {
            setState(s => ({ ...s, rejectInProgress: false }))
            throw error;
          }
        },
        [setState],
      )

      const reactivateUser = useCallback(
        async (id: any, isSuperAdmin: boolean = false) => {
          setState(s => ({ ...s, reactivateInProgress: true }))
          await axios.post(isSuperAdmin ? '/berryAPI/superadmin/reactivate' : '/berryAPI/user/reactivate',{}, {
            params: {id}
          })
          setState(s => ({ ...s, reactivateInProgress: false }))
        },
        [setState],
      )

      const resendSetupEmail = useCallback(async (email: string) => {
        const response = await axios.post('/berryAPI/email/resendSetup', { email });
        return response.data;
      }, []);

      const deleteProfilePhoto = useCallback(async (id: number) => {
        setState(s => ({ ...s, profilePhotoInProgress: true }));
        const formData = new FormData();
        formData.append('userId', id.toString());

        try {
          const response = await axios.post('/berryAPI/user/deleteProfilePhoto', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          setState(s => ({
            ...s,
            profilePhotoInProgress: false,
            user: s.user ? { ...s.user, image_url: null } : s.user,
          }));
          return response.data?.imageUrl ?? null;
        } catch (error) {
          setState(s => ({ ...s, profilePhotoInProgress: false }));
          throw error;
        }
      }, [setState]);

    return { ...state, fetch, createUser, updateUser, approvePendingProfileChanges, deleteUser, approveUser, rejectUser, reactivateUser, resendSetupEmail, uploadProfilePhoto, deleteProfilePhoto }
};

// Users may change their active role (e.g. if a recipient user has delivery driver access)
export const useUserActiveRole = () => {
  
  const changeActiveRole = async (active_role: any) => {
    await axios.post<any>('/berryAPI/user/editActiveRole', {
      active_role
    }).catch(function (error) {
      throw error;
    });
  };

  return { changeActiveRole }
}
